<template>
  <div class="market-switcher-drawer critical-component-hide">
    <p
      v-if="display === 'languages'"
      class="market-switcher-drawer__title text-heading-m"
      v-text="$string('market_switcher.drawer.select_language')"
    />
    <div v-else>
      <div class="market-switcher-drawer__form">
        <icon-search />

        <input
          ref="searchInput"
          type="search"
          placeholder="Search"
          class="market-switcher-drawer__search text-body-s"
          autocomplete="off"
          @input="debouncedSearch"
        >
      </div>
    </div>

    <hr class="market-switcher-drawer__divider">

    <ul
      v-if="display === 'languages'"
      class="market-switcher-drawer__items"
    >
      <li
        v-for="(language) of languages"
        :key="`language-${language.code}`"
        class="market-switcher-drawer__item"
      >
        <input
          :id="`language-${language.code}`"
          v-model="localLanguage"
          class="market-switcher-drawer__input"
          autocomplete="off"
          :lang="language.code"
          :name="isActive ? 'locale_code' : null"
          type="radio"
          :value="language.code"
          @change="submitForm(language)"
        >

        <label
          :for="`language-${language.code}`"
          class="market-switcher-drawer__label"
        >
          <span
            class="text-body-s"
            v-text="language.label"
          />
        </label>
      </li>
    </ul>

    <ul
      v-else
      class="market-switcher-drawer__items"
    >
      <li
        v-for="(country) of countriesData"
        :key="`country-${country.code}`"
        class="market-switcher-drawer__item"
      >
        <input
          :id="`country-${country.code}`"
          v-model="localCountry"
          class="market-switcher-drawer__input"
          autocomplete="off"
          :name="isActive ? 'country_code' : null"
          type="radio"
          :value="country.code"
          @change="submitForm(country)"
        >

        <label
          :for="`country-${country.code}`"
          class="market-switcher-drawer__label"
        >
          <img
            class="market-switcher-drawer__flag"
            alt=""
            :src="country.flagSrc"
          >

          <span
            class="text-body-s"
            v-html="`${country.name} (${country.currencySymbol})`"
          />
        </label>
      </li>
    </ul>
  </div>
</template>

<script>

/**
 * Vue: Market switcher drawer (market-switcher-drawer)
 * -----------------------------------------------------------------------------
 * Drawer which contains the market switcher.
 *
 * @param {Array<Object>} countries - Array of countries from localization.
 * @param {HTMLElement} form - Localization form.
 * @param {Boolean} isActive - Overlay is active.
 * @param {Array<Object>} languages - Array of languages from localization.
 *
 */
import { mapActions, mapState } from 'vuex'
import fuzzysort from 'fuzzysort'
import { debounce } from '~/helpers/general'

import IconSearch from '~icons/misc/search.svg'

export default {
  name: 'MarketSwitcherDrawer',

  components: {
    IconSearch,
  },

  props: {
    countries: {
      type: Array,
      default: () => [],
      required: true,
    },
    form: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: true,
      required: true,
    },
    languages: {
      type: Array,
      default: () => [],
      required: true,
    },
  },

  data() {
    return {
      localCountry: '',
      localLanguage: '',
      countriesData: this.countries,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      currentCountry: (state) => state['market-switcher'].currentCountry,
      currentLanguage: (state) => state['market-switcher'].currentLanguage,
      display: (state) => state['market-switcher'].display,
      settings: (state) => state.index.settings,
    }),
  },

  watch: {

    /**
     * Watch current country state to set local model value.
     */
    currentCountry: {
      handler() {
        this.localCountry = this.currentCountry?.code || ''
      },
      immediate: true,
    },

    /**
     * Watch current language state to set local model value.
     */
    currentLanguage: {
      handler() {
        this.localLanguage = this.currentLanguage?.code || ''
      },
      immediate: true,
    },
  },

  created(){
    this.debouncedSearch = debounce(this.search, this.settings.throttle)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setPreference: 'market-switcher/setPreference',
    }),

    /**
     * Submit form.
     * @param {Object} object - Chosen country/language.
     */
    submitForm(object) {
      if (!this.form) {
        return
      }

      if (object.type === 'country') {
        this.setPreference(object)
      }

      if(object.url && this.form instanceof HTMLFormElement) {
        this.form.setAttribute('action', `${object.url}/localization`)
      }

      this.form.submit()
    },

    /**
     * Search countries for a match & sets countriesData.
     * @param {Event} event - Input event.
     */
    search(event) {
      const query = event?.target?.value || ''

      if (!query) {
        this.countriesData = this.countries
        return
      }

      const results = fuzzysort.go(query, this.countries, {
        limit: 10,
        keys: ['name'],
      })

      this.countriesData = results.map((result) => result.obj)
    },
  },
}
</script>

<style lang="scss">
@import './market-switcher-drawer';
</style>
